import { GEAR_CAPS, HEROES, LEVEL_STATS, LEVEL_STATS_FULL_AT, RESPEC, SKILL_LOOK, SKILL_RANK_LEVELS, SKILL_TREES, TOMES, lookOf, type ForkId, type SkillDef, type SkillId, type SkillNode, GLYPH, GLYPH_IDS, SAGA_CHAPTERS } from '@somnhold/content';
import { gearMods, gearModsUncapped, heroStats, levelStat, knownSkills, tomeFull, tomeSkill, pointsSpent, rankAt, skillAt, type CampaignCommand, type CampaignState, type PlayerState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { getLang, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

type Cmd = (c: CampaignCommand) => Promise<boolean>;

const PERK_ICON: Record<string, string> = { repairDiscount: ICONS.repair, workBonus: ICONS.hammer, ghostPower: ICONS.ghost, incursionLoot: ICONS.chest };
/** Each fork gets its own icon, so the two sides of a choice read apart. */
export const FORK_ICON: Record<ForkId, string> = {
  bashCharge: ICONS.haste,
  bashCleave: ICONS.sword,
  ironWall: ICONS.shield,
  warCry: ICONS.challenge,
  sharedOath: ICONS.oath,
  secondWind: ICONS.heart,
  frostTurret: ICONS.frostWall,
  fireTurret: ICONS.fireball,
  shrapnel: ICONS.sword,
  magnet: ICONS.target,
  colossus: ICONS.shield,
  swarm: ICONS.multishot,
  meteor: ICONS.fireball,
  embers: ICONS.hourglass,
  longWall: ICONS.frostWall,
  bitingFrost: ICONS.sword,
  stillStorm: ICONS.hourglass,
  thunderclap: ICONS.storm,
  volley: ICONS.multishot,
  frostArrows: ICONS.frostWall,
  wideNet: ICONS.net,
  steelNet: ICONS.shield,
  pinningRain: ICONS.hourglass,
  wideRain: ICONS.arrowRain,
  mendChain: ICONS.mend,
  mendWard: ICONS.shield,
  consecrate: ICONS.fireball,
  bulwark: ICONS.shield,
  dawnRebirth: ICONS.heart,
  dawnSmite: ICONS.sword,
  whirlwind: ICONS.whirl,
  rend: ICONS.sword,
  quake: ICONS.leap,
  doubleLeap: ICONS.haste,
  bloodlust: ICONS.frenzy,
  warpath: ICONS.challenge,
  judgment: ICONS.smite,
  healingLight: ICONS.heart,
  reprisal: ICONS.sword,
  faithWall: ICONS.aegis,
  holyGround: ICONS.hallow,
  faithWeight: ICONS.hourglass,
  strangle: ICONS.entangle,
  spores: ICONS.hourglass,
  direBear: ICONS.bearForm,
  wildHeart: ICONS.heart,
  spikedDecoy: ICONS.thornWall,
  twinDecoy: ICONS.decoy,
  orbBurst: ICONS.storm,
  deepOrb: ICONS.hourglass,
  talons: ICONS.sword,
  hawkEye: ICONS.target,
  mendingTotem: ICONS.heart,
  lightTotem: ICONS.smite,
  stunHammer: ICONS.haste,
  wideHammer: ICONS.whirl,
  dreadRoar: ICONS.skull,
  longRoar: ICONS.hourglass,
  welderDrone: ICONS.haste,
  armorDrone: ICONS.shield,
  jumpingWasps: ICONS.leap,
  denseWasps: ICONS.multishot,
  crippleBlow: ICONS.hourglass,
  exposeBlow: ICONS.target,
  longArc: ICONS.storm,
  overcharge: ICONS.haste,
  piercingVenom: ICONS.multishot,
  virulentArrow: ICONS.hourglass,
  brightRay: ICONS.sanctuary,
  mendingRay: ICONS.heart,
  bleedingAxe: ICONS.sword,
  cleavingAxe: ICONS.whirl,
  searingLight: ICONS.smite,
  wideLight: ICONS.hallow,
  clusterMine: ICONS.multishot,
  tarMine: ICONS.hourglass,
  longCharge: ICONS.haste,
  crushingCharge: ICONS.shield,
  barkskin: ICONS.shield,
  spreadingRoots: ICONS.bloom,
  deepGrove: ICONS.bloom,
  briars: ICONS.thornWall,
  rallyBanner: ICONS.heart,
  wideBanner: ICONS.hallow,
  whiteout: ICONS.storm,
  deepFreeze: ICONS.hourglass,
  pinningArrow: ICONS.target,
  burstingArrow: ICONS.multishot,
  mercifulDawn: ICONS.heart,
  blindingDawn: ICONS.smite,
  sacredRampart: ICONS.shield,
  longRampart: ICONS.hourglass,
  heavyMortar: ICONS.target,
  incendiaryMortar: ICONS.multishot,
  virulentPlague: ICONS.skull,
  witheringPlague: ICONS.hourglass,
  heavyChain: ICONS.shield,
  twinChain: ICONS.multishot,
  deepRepair: ICONS.repair,
  wideRepair: ICONS.multishot,
  longLance: ICONS.target,
  bitingLance: ICONS.frostWall,
  heavyShot: ICONS.target,
  pinShot: ICONS.hourglass,
  longRenew: ICONS.hourglass,
  sharedRenew: ICONS.bloom,
  executeBlow: ICONS.skull,
  stunBlow: ICONS.haste,
  brightSeal: ICONS.heart,
  burningSeal: ICONS.smite,
  longLash: ICONS.target,
  deepThorns: ICONS.thornWall,
  wideShout: ICONS.hallow,
  longShout: ICONS.hourglass,
  bigCoil: ICONS.multishot,
  fastCoil: ICONS.haste,
  frostBlink: ICONS.frostWall,
  farBlink: ICONS.target,
  barbedSnare: ICONS.sword,
  twinSnare: ICONS.multishot,
  deepPrayer: ICONS.heart,
  wardPrayer: ICONS.shield,
  longLetting: ICONS.hourglass,
  thirstyLetting: ICONS.heart,
  steadfast: ICONS.shield,
  healingStep: ICONS.heart,
  longThorns: ICONS.target,
  sharpThorns: ICONS.sword,
  sharedStand: ICONS.shield,
  longStand: ICONS.hourglass,
  ironHound: ICONS.shield,
  packHound: ICONS.multishot,
  emberStorm: ICONS.haste,
  greatMeteors: ICONS.fireball,
  longEye: ICONS.hourglass,
  keenEye: ICONS.target,
  longIntercession: ICONS.hourglass,
  fullIntercession: ICONS.heart,
  longFury: ICONS.hourglass,
  wideFury: ICONS.whirl,
  radiantAvatar: ICONS.smite,
  guardianAvatar: ICONS.shield,
  ancientTreant: ICONS.antlers,
  threeTreants: ICONS.multishot,
  wideSlam: ICONS.hallow,
  hardSlam: ICONS.shieldBash,
  bulwarkWall: ICONS.shield,
  wideWall: ICONS.hallow,
  thunderEcho: ICONS.storm,
  longEcho: ICONS.hourglass,
  longFlame: ICONS.target,
  napalm: ICONS.fireball,
  longBarricade: ICONS.multishot,
  spikedBarricade: ICONS.sword,
  reinforce: ICONS.repair,
  longFortify: ICONS.hourglass,
  corrosiveDart: ICONS.hourglass,
  splashDart: ICONS.multishot,
  widerChains: ICONS.multishot,
  heavyChains: ICONS.skull,
  deepRift: ICONS.skull,
  wideRift: ICONS.hallow,
  farLeap: ICONS.leap,
  sharpCaltrops: ICONS.sword,
  brightFlare: ICONS.hallow,
  burningFlare: ICONS.fireball,
  longHidden: ICONS.hourglass,
  deadlyHidden: ICONS.target,
  brightLance: ICONS.smite,
  mercyLight: ICONS.heart,
  wideCleanse: ICONS.hallow,
  wardCleanse: ICONS.shield,
  longChoir: ICONS.hourglass,
  loudChoir: ICONS.heart,
  farKick: ICONS.leap,
  bruteKick: ICONS.sword,
  wideTerrify: ICONS.hallow,
  deepTerrify: ICONS.skull,
  longDuel: ICONS.hourglass,
  bloodDuel: ICONS.heart,
  greaterAtone: ICONS.heart,
  sharedAtone: ICONS.bloom,
  longBlessing: ICONS.hourglass,
  brightBlessing: ICONS.smite,
  longSacrifice: ICONS.hourglass,
  wardedSacrifice: ICONS.shield,
  thickSpores: ICONS.hallow,
  venomSpores: ICONS.skull,
  longWolf: ICONS.hourglass,
  swiftWolf: ICONS.haste,
  longQuake: ICONS.hourglass,
  shatterQuake: ICONS.skull,
};

// Kept across rerenders of the sheet: the tab, the skill being looked at, the node just learned, and
// whether the start-over button is waiting for its confirmation.
let tab: 'skills' | 'bastion' = 'skills';
let picked: SkillId | null = null;
let flash: string | null = null;
let confirmRespec = false;

/** The looks that take part in a reaction. */
const REACTS: Record<string, true> = { frost: true, bolt: true, fire: true, poison: true };

export { SKILL_LOOK, lookOf };

const num = (v: number, digits = 1): string => v.toLocaleString(getLang() === 'es' ? 'es-AR' : 'en-US', { maximumFractionDigits: digits });
const sec = (ticks: number): string => t('unit.sec', { n: num(ticks / 12) });
const hexes = (fixed: number): string => t('unit.hexes', { n: num(fixed / 1000) });

const amountText = (skill: string, v: number): string => (skill === 'dawn' || skill === 'bloom' ? `${num(v / 100)} %` : String(v));

/** A skill's numbers in one line ("Damage 120 · Area 3 hexes · Cooldown 60 s"), for tooltips in battle. */
export const skillFacts = (id: string, sk: SkillDef, cooldownBp: number): string => {
  const out: string[] = [];
  if (sk.damage) out.push(`${t('tree.f.damage')} ${sk.damage}`);
  if (sk.amount) out.push(`${t(`tree.amount.${id}`)} ${amountText(id, sk.amount)}`);
  if (sk.duration) out.push(`${t('tree.f.duration')} ${sec(sk.duration)}`);
  if (sk.stun) out.push(`${t('tree.f.stun')} ${sec(sk.stun)}`);
  if (sk.radius) out.push(`${t('tree.f.radius')} ${hexes(sk.radius)}`);
  if (sk.castRange) out.push(`${t('tree.f.reach')} ${hexes(sk.castRange)}`);
  out.push(`${t('tree.f.cooldown')} ${sec((sk.cooldown * cooldownBp) / 10000)}`);
  return out.join(' · ');
};

/** A skill's numbers as [label, value] rows, at the hero's level and with a variant (or none). */
const factRows = (id: SkillId, sk: SkillDef, cooldownBp: number): [string, string][] => {
  const rows: [string, string][] = [];
  if (sk.damage) rows.push([t('tree.f.damage'), String(sk.damage)]);
  if (sk.amount) rows.push([t(`tree.amount.${id}`), amountText(id, sk.amount)]);
  if (sk.duration) rows.push([t('tree.f.duration'), sec(sk.duration)]);
  if (sk.stun) rows.push([t('tree.f.stun'), sec(sk.stun)]);
  if (sk.radius) rows.push([t('tree.f.radius'), hexes(sk.radius)]);
  if (sk.castRange) rows.push([t('tree.f.reach'), hexes(sk.castRange)]);
  rows.push([t('tree.f.cooldown'), sec((sk.cooldown * cooldownBp) / 10000)]);
  return rows;
};

/** What a variant changes against the skill alone: only the numbers that move, [label, alone, with]. */
const variantDiff = (id: SkillId, alone: SkillDef, withV: SkillDef, cooldownBp: number): [string, string, string][] => {
  const a = factRows(id, alone, cooldownBp);
  const b = new Map(factRows(id, withV, cooldownBp));
  return a.filter(([k, v]) => b.has(k) && b.get(k) !== v).map(([k, v]) => [k, v, b.get(k) as string]);
};

const slotOf = (cls: PlayerState['hero']['heroClass'], skill: SkillId): 0 | 1 | 2 => {
  const own = HEROES[cls].skills.indexOf(skill);
  if (own >= 0) return own as 0 | 1 | 2;
  return (SKILL_TREES[cls].find((n) => n.effect.kind === 'learn' && n.effect.skill === skill)?.branch ?? 0) as 0 | 1 | 2;
};

/**
 * Armor and cooldown as one total (the same as the sheet's numbers above it), and under it where it comes from:
 * the class's own, gear (held at its cap, with what goes past it wasted) and the level's share, which
 * fills on its own by LEVEL_STATS_FULL_AT.
 */
export const statCaps = (hero: PlayerState['hero']): HTMLElement => {
  const raw = gearModsUncapped(hero);
  const pct = (bp: number): string => num(bp / 100);
  const row = (key: 'armor' | 'cooldown', icon: string, base: number, gearHave: number, gearCap: number): HTMLElement => {
    const levelCap = LEVEL_STATS[hero.heroClass][key] ?? 0;
    const gear = Math.min(gearHave, gearCap);
    const level = levelStat(hero.heroClass, hero.level, key);
    const over = Math.max(0, gearHave - gearCap);
    const most = base + gearCap + levelCap;
    return h(
      'div.pcap-row',
      { 'data-cap': key },
      h('div.pcap-head', {}, h('span.pcap-name', {}, h('span.stat-ico', { html: icon }), t(`hero.stat.${key}`)), h('b.pcap-total', {}, t(`tree.capTotal.${key}`, { n: num((base + gear + level) / 100, 0) }))),
      h('div.bar.pcap-bar', {}, base ? h('i.base', { style: `width:${(100 * base) / most}%` }) : '', h('i.gear', { style: `width:${(100 * gear) / most}%` }), h('i.level', { style: `width:${(100 * level) / most}%` })),
      h(
        'span.small.pcap-src',
        {},
        base ? h('span', {}, h('span.pcap-base', {}, t('tree.capClass', { n: pct(base) })), ' + ') : '',
        h('span.pcap-gear', {}, t(gear >= gearCap ? 'tree.capGearFull' : 'tree.capGear', { n: pct(gear), m: pct(gearCap) })),
        ' + ',
        h('span.pcap-level', {}, levelCap ? t('tree.capLevel', { n: pct(level), m: pct(levelCap), l: LEVEL_STATS_FULL_AT }) : t('tree.capNoLevel')),
        over > 0 ? h('b.pcap-over', {}, ' · ', t('tree.capOver', { n: pct(over) })) : '',
      ),
    );
  };
  return h(
    'div.pcaps',
    { 'data-test': 'stat-caps' },
    h('h5', {}, t('tree.caps')),
    row('armor', ICONS.shield, HEROES[hero.heroClass].armorBp, raw.armorBp, GEAR_CAPS.armorBp),
    row('cooldown', ICONS.hourglass, 0, raw.cooldownBp, GEAR_CAPS.cooldownBp),
  );
};

/**
 * The skill tree on the hero sheet (docs/PODERES.md §2): three trees that grow upwards, one per slot,
 * with the slots' names as roots. Each skill carries its variants as two leaves; touching a skill
 * shows them side by side (what changes, what it's good for) with "Learn" on the ones not known yet.
 * The bastion perks have their own tab.
 */
export const skillTree = (s: CampaignState, p: PlayerState, cmd: Cmd, rerender: () => void): HTMLElement => {
  const hero = p.hero;
  const cls = hero.heroClass;
  const over = s.status !== 'active';
  const spent = pointsSpent(hero);
  const free = over ? 0 : hero.skillPoints - spent;
  const nodes = SKILL_TREES[cls];
  const rankOf = (n: SkillNode): number => hero.skills[n.id] ?? 0;
  const stats = heroStats({ pid: p.id, name: '', cls, level: hero.level, control: 'live', powerBp: 10000, post: 0, gear: gearMods(hero) });
  const variantsOf = (skill: SkillId): SkillNode[] => nodes.filter((n) => n.effect.kind === 'variant' && n.effect.skill === skill);
  const learnNode = (skill: SkillId): SkillNode | undefined => nodes.find((n) => n.effect.kind === 'learn' && n.effect.skill === skill);
  const knows = (skill: SkillId): boolean => knownSkills(hero, slotOf(cls, skill)).includes(skill);
  if (!picked || slotOf(cls, picked) === undefined || !(HEROES[cls].skills.includes(picked) || learnNode(picked))) picked = HEROES[cls].skills[0];

  const learn = async (ev: Event, n: SkillNode): Promise<void> => {
    (ev.currentTarget as HTMLButtonElement).disabled = true;
    if (await cmd({ t: 'skill', node: n.id })) {
      flash = n.id;
      audio.play('build', 0.5);
    }
    rerender();
  };

  const wrap = h('div.skilltree');
  wrap.append(
    h(
      'div.sec-title',
      {},
      h('h4', {}, t('hero.tree')),
      h(`span.points${free > 0 ? '.has' : ''}`, {}, t(free === 1 ? 'hero.point' : 'hero.points', { n: free })),
    ),
    h(
      'div.ptabs',
      { role: 'tablist' },
      ...(['skills', 'bastion'] as const).map((k) =>
        h(`button.ptab${tab === k ? '.on' : ''}`, { role: 'tab', 'aria-selected': String(tab === k), 'data-tab': k, onclick: () => ((tab = k), (confirmRespec = false), rerender()) }, t(`tree.tab.${k}`)),
      ),
    ),
  );

  if (tab === 'skills' && !over) wrap.append(h('p.hint', {}, t('hero.pointsHint')));
  if (tab === 'skills') {
    // The trees: each slot's skills from the top (the last to open) down to the class's own at the root.
    const rank = rankAt(hero.level);
    const nextRank = SKILL_RANK_LEVELS.find((l) => hero.level < l);
    const grove = h('div.ptree');
    ([0, 1, 2] as const).forEach((slot) => {
      const own = HEROES[cls].skills[slot];
      // The tome's skill on top (it is the last to come), then the rest from the highest level down.
      const later = nodes
        .filter((n) => n.branch === slot && n.effect.kind === 'learn')
        .sort((a, b) => Number(!!(b.effect as { tome?: true }).tome) - Number(!!(a.effect as { tome?: true }).tome) || (b.effect as { level: number }).level - (a.effect as { level: number }).level);
      const trunk = h('div.ptrunk');
      const node = (skill: SkillId, opensAt: number): HTMLElement => {
        const known = knows(skill);
        const ln = learnNode(skill);
        const tome = !!ln && ln.effect.kind === 'learn' && !!ln.effect.tome;
        const can = !known && !!ln && !tome && free > 0 && hero.level >= opensAt;
        // Reached but no point to spend: not a lock, so it doesn't read as "Level 14" at level 20.
        const broke = !known && !!ln && !tome && !over && free <= 0 && hero.level >= opensAt;
        const state = known ? 'known' : can ? 'can' : broke ? 'broke' : tome ? 'tome' : 'lock';
        const vs = variantsOf(skill);
        const have = vs.filter((v) => rankOf(v) > 0).length;
        // The variants hang off the skill, named: learned ones lit, the rest learnable right here.
        const leaves = h(
          'div.pleaves',
          {},
          ...vs.map((v) => {
            const fork = (v.effect as { fork: ForkId }).fork;
            const on = rankOf(v) > 0;
            const canV = !on && known && !over && free > 0;
            return h(
              `button.pleaf${on ? '.on' : canV ? '.can' : ''}${flash === v.id ? '.just' : ''}`,
              {
                'data-variant': fork,
                title: t(`fork.${fork}.desc`),
                onclick: (ev: Event) => {
                  picked = skill;
                  confirmRespec = false;
                  if (canV) void learn(ev, v);
                  else rerender();
                },
              },
              h('span.pleaf-ico', { html: FORK_ICON[fork] }),
              h('span', {}, t(`fork.${fork}`)),
              canV ? h('b', {}, '+') : '',
            );
          }),
        );
        const btn = h(
          `button.pnode.s-${state}.el-${SKILL_LOOK[skill]}${picked === skill ? '.picked' : ''}${vs.some((v) => v.id === flash) ? '.just' : ''}`,
          { 'data-skill': skill, 'aria-pressed': String(picked === skill), onclick: () => ((picked = skill), (confirmRespec = false), audio.play('click', 0.4), rerender()) },
          h(
            'span.pgem',
            {},
            h('span.gem-ico', { html: known || can || broke ? ICONS[skill] : tome ? ICONS.tome : ICONS.lock }),
            ...vs.slice(0, 2).map((v, i) => h(`i.leaf.${i ? 'r' : 'l'}${rankOf(v) > 0 ? '.on' : ''}`)),
          ),
          h('span.pname', {}, t(`skill.${skill}`)),
          h('span.pstate', {}, known ? (vs.length ? t('tree.leaves', { n: have, m: vs.length }) : '') : can ? t('tree.variant.learn') : broke ? t('tree.noPoint') : tome ? t('tree.tomePages', { n: hero.tomePages?.[slot] ?? 0, m: TOMES.pages }) : t('tree.lockedAt', { l: opensAt })),
        );
        return h('div.pbranch', {}, btn, known && vs.length ? leaves : '');
      };
      for (const n of later) trunk.append(node((n.effect as { skill: SkillId }).skill, (n.effect as { level: number }).level));
      trunk.append(node(own, 1));
      trunk.append(
        h('div.proot', {}, h('b', {}, t(`tree.slot.${slot}`)), h('span', {}, t('tree.rankNow', { n: ['I', 'II', 'III'][rank - 1] as string }))),
      );
      grove.append(trunk);
    });
    wrap.append(grove);
    // Points nothing in the tree can take yet: say so, so they don't look lost.
    const open = nodes.filter((n) => n.effect.kind !== 'learn' && rankOf(n) < n.maxRank && (n.effect.kind !== 'variant' || knows(n.effect.skill))).reduce((a, n) => a + n.maxRank - rankOf(n), 0);
    if (free > open) wrap.append(h('p.hint.pspare', {}, t('tree.spare', { n: free - open })));
    if (nextRank) wrap.append(h('p.small.prank', {}, t('tree.rankNext', { n: ['I', 'II', 'III'][rank] as string, l: nextRank })));

    // The card of the skill being looked at: its numbers, then the ways to carry it.
    const id = picked as SkillId;
    const slot = slotOf(cls, id);
    const alone = skillAt(cls, hero.level, slot, null, id);
    const ln = learnNode(id);
    const known = knows(id);
    const card = h(
      `div.tree-card.pcard.el-${SKILL_LOOK[id]}`,
      {},
      h(
        'div.tc-head',
        {},
        h('span.gem', {}, h('span.gem-ico', { html: ICONS[id] })),
        h('div', {}, h('b', {}, t(`skill.${id}`)), h('div.small', {}, t(`tree.slot.${slot}`), ' · ', t('tree.rankNow', { n: ['I', 'II', 'III'][rankAt(hero.level) - 1] as string }))),
      ),
      h('p.tc-desc', {}, t(`skill.${id}.desc`, { n: alone.amount, s: alone.duration / 12 })),
      // What it reacts with (docs/PODERES.md §4): carrying things that go together pays.
      REACTS[SKILL_LOOK[id]] ? h('p.small.preact', {}, h('b', {}, t(`react.${SKILL_LOOK[id]}.name`)), ' ', t(`react.${SKILL_LOOK[id]}`)) : '',
      h('div.tc-rows', {}, ...factRows(id, alone, stats.cooldownBp).map(([label, v]) => h('div.tc-row.two', {}, h('span', {}, label), h('b.tc-b', {}, v)))),
    );
    if (!known && ln && ln.effect.kind === 'learn' && ln.effect.tome) {
      // A tome's skill: how many pages there are, and where the rest are found.
      const n = hero.tomePages?.[slot] ?? 0;
      card.append(
        h('div.ptome', {}, h('span.ptome-ico', { html: ICONS.tome }), h('div', {}, h('b', {}, t('tree.tome', { s: t(`skill.${id}`), n, m: TOMES.pages })), h('span.small', {}, t(`tree.tomeWhere.${slot}`)))),
        h('div.ptome-pages', {}, ...Array.from({ length: TOMES.pages }, (_, i) => h(`i${i < n ? '.on' : ''}`))),
      );
    } else if (!known && ln) {
      const lvl = (ln.effect as { level: number }).level;
      card.append(
        hero.level < lvl
          ? h('p.small.tc-why', {}, t('tree.lockedAt', { l: lvl }))
          : free <= 0
            ? h('p.small.tc-why', {}, t('tree.why.noPoints'))
            : h('button.btn.tc-learn', { 'data-act': 'learn', onclick: (ev: Event) => void learn(ev, ln) }, t('tree.variant.learn')),
      );
    }
    // Here only what's still to learn (choosing which to carry is "What to carry", below: never twice the same).
    // Not known yet: the variants still show, to weigh them before spending, without "Learn".
    const missing = variantsOf(id).filter((v) => rankOf(v) === 0);
    if (variantsOf(id).length) {
      if (missing.length) card.append(h('div.tc-cap.pways', {}, t(known ? 'tree.toLearn' : 'tree.variantsLater')));
      for (const v of missing) {
        const fork = (v.effect as { fork: ForkId }).fork;
        const diff = variantDiff(id, alone, skillAt(cls, hero.level, slot, fork, id), stats.cooldownBp);
        card.append(
          h(
            `div.pvar${flash === v.id ? '.just' : ''}`,
            { 'data-variant': fork },
            h('span.pvar-ico', { html: FORK_ICON[fork] }),
            h(
              'div.pvar-txt',
              {},
              h('b', {}, t(`fork.${fork}`)),
              h('span', {}, t(`fork.${fork}.desc`)),
              diff.length ? h('span.pvar-diff', {}, ...diff.map(([k, a, b]) => h('span', {}, `${k} `, h('s', {}, a), ' → ', h('b', {}, b)))) : '',
              h('span.pvar-when', {}, t('tree.when', { w: t(`fork.${fork}.when`) })),
            ),
            over || !known ? '' : h('button.btn.small.pvar-learn', { 'data-act': 'learn', disabled: free <= 0, title: free <= 0 ? t('tree.why.noPoints') : '', onclick: (ev: Event) => void learn(ev, v) }, t('tree.variant.learn')),
          ),
        );
      }
      if (known) card.append(h('p.small.pcarry', {}, missing.length ? t('tree.carryHint') : t('tree.allLearned')));
    }
    wrap.append(card);
  } else {
    // The bastion: four perks, two ranks each, in order.
    const perks = nodes.filter((n) => n.branch === 3).sort((a, b) => a.tier - b.tier);
    const list = h('div.pperks');
    perks.forEach((n, i) => {
      const e = n.effect as { perk: string; bpPerRank: number };
      const r = rankOf(n);
      const open = i === 0 || rankOf(perks[i - 1] as SkillNode) > 0;
      const can = !over && free > 0 && open && r < n.maxRank;
      list.append(
        h(
          `div.pperk${flash === n.id ? '.just' : ''}`,
          { 'data-node': n.id },
          h('span.pvar-ico', { html: PERK_ICON[e.perk] as string }),
          h(
            'div.pvar-txt',
            {},
            h('b', {}, t(`perk.${e.perk}.name`)),
            // What the learned ranks add up to (one rank's worth before learning any), and the next rank.
            h('span', {}, t(`perk.${e.perk}`, { v: (Math.max(r, 1) * e.bpPerRank) / 100 })),
            r > 0 && r < n.maxRank ? h('span.pvar-diff', {}, t('tree.perkNext', { n: r + 1, what: t(`perk.${e.perk}`, { v: ((r + 1) * e.bpPerRank) / 100 }) })) : '',
          ),
          h('span.npips', {}, ...Array.from({ length: n.maxRank }, (_, k) => h(`i${k < r ? '.on' : ''}`))),
          can
            ? h('button.btn.small.pvar-learn', { 'data-act': 'learn', onclick: (ev: Event) => void learn(ev, n) }, t('tree.variant.learn'))
            : !open && r === 0
              ? h('span.small', {}, t('tree.why.locked', { prev: t(`perk.${(perks[i - 1]?.effect as { perk: string }).perk}.name`) }))
              : '',
        ),
      );
    });
    wrap.append(list);
  }
  flash = null;

  // Starting over: free after each level-up, dust otherwise; always asks first.
  const cost = hero.freeRespec ? 0 : RESPEC.dustPerPoint * spent;
  if (!over && spent > 0)
    wrap.append(
      confirmRespec
        ? h(
            'div.respec.ask',
            {},
            h('span.small', {}, t('tree.respecAsk', { n: spent })),
            h(
              'button.btn.small',
              {
                'data-act': 'respec-yes',
                disabled: cost > s.storage.dust,
                onclick: async () => {
                  confirmRespec = false;
                  if (await cmd({ t: 'respec' })) audio.play('crumble', 0.4);
                  rerender();
                },
              },
              t('tree.respecYes'),
            ),
            h('button.btn.small.ghost', { onclick: () => ((confirmRespec = false), rerender()) }, t('tree.respecNo')),
          )
        : h(
            'button.btn.small.ghost.respec',
            { 'data-act': 'respec', onclick: () => ((confirmRespec = true), rerender()) },
            t('tree.respec'),
            ' ',
            cost === 0 ? h('span.free', {}, t('tree.free')) : h('span.cost-chip', {}, h('span', { html: ICONS.dust }), String(cost)),
          ),
    );
  return wrap;
};

/**
 * The glyph stone by the keep (docs/GLIFOS.md §4): a mark for each glyph a hero of the group completed, and a scratch
 * while someone is still gathering fragments. Null: nothing to show yet.
 */
export const glyphStone = (s: CampaignState): { marks: string[]; carving: boolean; clan?: boolean; remedies?: boolean } | null => {
  const marks: string[] = [];
  let carving = false;
  for (const p of s.players)
    for (const g of GLYPH_IDS) {
      const n = p.hero.glyphFrags?.[g] ?? 0;
      if (n >= GLYPH.fragments) marks.push(g);
      else if (n > 0) carving = true;
    }
  // The saga's glyphs given whole (docs/UNICAS.md §1): their trophies stand by the stone once the chapter is done.
  const past = (id: string): boolean => (s.saga?.chapter ?? 0) > SAGA_CHAPTERS.findIndex((c) => c.id === id);
  const clan = past('deep');
  const remedies = past('maelis');
  return marks.length || carving || clan || remedies ? { marks, carving, ...(clan ? { clan } : {}), ...(remedies ? { remedies } : {}) } : null;
};

/**
 * The tomes' shelf by the keep (docs/PODERES.md §6): a book for each tome anyone in the group has filled, in
 * its skill's colour, and an open book while someone is still gathering pages. Null: nothing to show yet.
 */
export const tomeShelf = (s: CampaignState): { books: string[]; reading: boolean } | null => {
  const books: string[] = [];
  let reading = false;
  for (const p of s.players) {
    for (const slot of [0, 1, 2] as const) {
      const id = tomeSkill(p.hero, slot);
      if (!id) continue;
      if (tomeFull(p.hero, slot)) books.push(SKILL_LOOK[id]);
      else if ((p.hero.tomePages?.[slot] ?? 0) > 0) reading = true;
    }
  }
  return books.length || reading ? { books, reading } : null;
};
